import type { Maybe } from '@adornis/base/utilTypes.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { html, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { CHANGE_OPERATION_PRESENTATION, getChangeOperation } from '../../../api/changePresentation.js';
import { getReadableValueOfChange } from '../../../api/getReadableValueOfChange.js';
import { formatTranslatedPathSegment, translateContractPathSegments } from '../../../api/translateContractPath.js';
import type { ContractSupplementApplyHistory } from '../../../entities/AppliedContract.js';
import { type ContractSupplementChange, ContractSupplementDataType } from '../../../entities/ContractSupplement.js';

type ChangeLike = NarrowedEntity<ContractSupplementChange, { _id: 1; path: 1; value: 1; type: 1 }>;

/**
 * Renders the translated change path as a breadcrumb. The leaf is the field the user actually changed, so it is
 * emphasized while its parents stay muted for orientation.
 */
const PathBreadcrumb = (path: string) => {
  const segments = translateContractPathSegments(path);

  return html`
    <div class="flex flex-row flex-wrap items-baseline gap-x-1 text-sm">
      ${repeat(
        segments,
        (segment, index) => `${segment.label}-${index}`,
        (segment, index) => html`
          ${index > 0 ? html`<span class="text-base-content/70">›</span>` : nothing}
          <span class="${index === segments.length - 1 ? 'font-semibold' : 'text-base-content/70'}">
            ${formatTranslatedPathSegment(segment)}
          </span>
        `,
      )}
    </div>
  `;
};

/**
 * Displays a single change of a supplement: what kind of change it is, which field it touches and how the value
 * develops. This is the one convention for changes across the module, used by the supplement panel, the
 * supplement creation dialog and the MAV process comments.
 */
export const ChangeEntry = (props: {
  change: Maybe<ChangeLike>;
  history?: Maybe<ContractSupplementApplyHistory>;
  /** compact spacing for the narrow supplement panel */
  dense?: boolean;
  deletable?: boolean;
  onDelete?: () => void;
}) => {
  if (!props.change) return nothing;

  const change = props.change;
  const historyForPath = props.history?.paths?.find(pathHistory => pathHistory.path === change.path);
  const presentation = CHANGE_OPERATION_PRESENTATION[getChangeOperation(change, historyForPath)];

  return html`
    <div class="flex flex-col gap-1 rounded-lg border ${presentation.containerClass} ${props.dense ? 'p-2' : 'p-3'}">
      <div class="flex flex-row items-center justify-between gap-2">
        <div class="flex flex-row items-center gap-1 ${presentation.textClass}">
          <span class="${presentation.iconClass} text-base"></span>
          <span class="text-sm font-semibold uppercase tracking-wide">${presentation.label}</span>
        </div>
        ${
          props.deletable
            ? html`
                <button
                  class="btn btn-ghost btn-xs btn-square rounded-full bg-base-100"
                  aria-label="Änderung löschen"
                  @click=${() => props.onDelete?.()}
                >
                  <span class="icon-[material-symbols--delete-outline] text-error text-base"></span>
                </button>
              `
            : nothing
        }
      </div>

      ${PathBreadcrumb(change.path)}
      ${
        change.type !== ContractSupplementDataType.NULL
          ? html`
              <div class="text-sm">${getReadableValueOfChange(change, { history: props.history ?? undefined })}</div>
            `
          : nothing
      }
    </div>
  `;
};

/**
 * Renders all changes of a supplement. Fields that only got created without a value carry no information for
 * the user, so they are filtered out here instead of at every call site.
 */
export const ChangeList = (props: {
  changes: Maybe<ChangeLike[]>;
  history?: Maybe<ContractSupplementApplyHistory>;
  dense?: boolean;
  deletable?: boolean;
  emptyState?: unknown;
  onDelete?: (change: ChangeLike) => void;
}) => {
  const changes = (props.changes ?? []).filter(change => change.type !== ContractSupplementDataType.NEW_FIELD);

  if (!changes.length) return props.emptyState ?? nothing;

  return html`
    <div class="flex flex-col ${props.dense ? 'gap-1.5' : 'gap-2'}">
      ${repeat(
        changes,
        // keyed by path, not by id: a change derived from the form gets a fresh id on every keystroke, which
        // would throw away and rebuild every card while the user is typing
        change => change.path,
        change =>
          ChangeEntry({
            change,
            history: props.history,
            dense: props.dense,
            deletable: props.deletable,
            onDelete: () => props.onDelete?.(change),
          }),
      )}
    </div>
  `;
};
